import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../auth/useAuthStore';
import './AdminEntryPage.css';

function isSuperAdminUser(user: { role: { name: string } }): boolean {
  return user.role.name === 'superadmin';
}

/**
 * Detecta si se está en un dominio distinto de admin.wayline.city
 * (p.ej. en wayline.city o localhost accediendo a /admin).
 * En producción sobre wayline.city raíz, redirige al subdominio canónico.
 */
function shouldRedirectToAdminSubdomain(): boolean {
  const { hostname } = window.location;
  // Solo redirige en producción y cuando NO está ya en admin.wayline.city
  if (hostname === 'localhost' || hostname === '127.0.0.1') return false;
  if (hostname.startsWith('admin.')) return false;
  // Si es wayline.city o cualquier otro dominio en producción → redirigir
  return hostname.includes('wayline.city') || !hostname.includes('localhost');
}

export function AdminEntryPage(): JSX.Element {
  const { isAuthenticated, isLoading, token, user } = useAuthStore();
  const navigate = useNavigate();

  // En producción desde wayline.city/admin → redirigir a admin.wayline.city
  useEffect(() => {
    if (shouldRedirectToAdminSubdomain()) {
      const adminUrl = `${window.location.protocol}//admin.wayline.city`;
      window.location.replace(adminUrl);
    }
  }, []);

  // Auto-redirect si ya hay sesión válida de superadmin
  useEffect(() => {
    if (!token || isLoading || !user) return;
    if (isSuperAdminUser(user)) {
      navigate('/admin/overview', { replace: true });
    }
  }, [token, isLoading, user, navigate]);

  // Mientras se resuelve la sesión o la redirección de subdominio está en curso
  if (token && (isLoading || !user)) {
    return (
      <div className="ae-layout">
        <div className="ae-spinner" aria-label="Verificando sesión…" />
      </div>
    );
  }

  // Autenticado pero sin rol superadmin
  if (isAuthenticated && user && !isSuperAdminUser(user)) {
    return (
      <div className="ae-layout">
        <div className="ae-content">
          <div className="ae-brand">
            <img
              src="/images/wayline-isotipo.svg"
              alt=""
              aria-hidden="true"
              className="ae-brand-mark"
            />
            <span className="ae-brand-name">WAYLINE</span>
            <span className="ae-brand-suffix">Admin</span>
          </div>
          <h1 className="ae-title">Acceso restringido</h1>
          <p className="ae-desc">Tu cuenta no tiene permisos para acceder a esta sección.</p>
          <button type="button" className="ae-btn" onClick={() => navigate('/territorio')}>
            Volver al proyecto
          </button>
        </div>
      </div>
    );
  }

  // Sin sesión — entrada de administración
  return (
    <div className="ae-layout">
      <div className="ae-content">
        <div className="ae-brand">
          <img
            src="/images/wayline-isotipo.svg"
            alt=""
            aria-hidden="true"
            className="ae-brand-mark"
          />
          <span className="ae-brand-name">WAYLINE</span>
          <span className="ae-brand-suffix">Admin</span>
        </div>
        <h1 className="ae-title">Administración de plataforma</h1>
        <p className="ae-desc">
          Área de gestión interna de Wayline. Acceso restringido a administradores.
        </p>
        <button
          type="button"
          className="ae-btn"
          onClick={() => navigate(`/login?next=${encodeURIComponent('/admin/overview')}`)}
        >
          Ingresar como administrador
        </button>
      </div>
    </div>
  );
}
